import {
  CheckOutlined,
  ClearOutlined,
  DownloadOutlined,
  EllipsisOutlined,
  InfoCircleOutlined,
  ScissorOutlined,
  StopOutlined,
  DownOutlined,
  UpOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, MenuProps, Modal, Space } from "antd";
import { getEntities, groupEvents } from "pages/api-clients/graph-search";
import { AnnotatedDeepLink } from "pages/api/deep-links/types";
import { QueryExplain } from "pages/[orgId]/explore/QueryExplain";
import { ReactNode, useState, useMemo, useCallback } from "react";
import { useApplicationContext } from "src/hooks/useApplication";
import { usePopupContext } from "src/hooks/usePopup";
import { SearchState } from "src/search/types";
import { downloadAsFile } from "src/utils/file";
import { SnippetForm } from "../../SnippetForm";
import { UseQuestionInputClauses } from "./definitions";
import styles from "./question-input.module.css";
import { CustomTooltip } from "src/components/CustomTooltip";

type MenuItem = Required<MenuProps>["items"][number];

function SnippetFormControl({
  clauses,
  onClose,
}: {
  clauses: UseQuestionInputClauses["list"];
  onClose(): void;
}) {
  return (
    <Modal
      maskClosable={false}
      open={true}
      onCancel={() => onClose()}
      footer={null}
    >
      <SnippetForm clauses={clauses} onFinish={onClose} />
    </Modal>
  );
}

export function Controls({
  applyLabel,
  clauses,
  collapsed,
  disabledApply,
  disabledCancel,
  onApply,
  onReset,
  resetIcon,
  resetTitle,
  searchState,
  isQueryEditor,
  inlineResetOnly,
  onToggleCollapse,
}: {
  applyLabel?: string;
  clauses: UseQuestionInputClauses;
  collapsed?: boolean;
  inlineResetOnly?: boolean;
  disabledApply?: boolean;
  disabledCancel?: boolean;
  onApply?: () => void;
  resetIcon?: ReactNode;
  resetTitle?: ReactNode;
  onReset(): void;
  searchState: SearchState;
  snippetTransformer?: (state: SearchState) => SearchState;
  snippetOptionFilter?: (sinppet?: AnnotatedDeepLink) => boolean;
  isQueryEditor?: boolean;
  onToggleCollapse: () => void;
}) {
  const app = useApplicationContext();
  const isSuperUser = app.user?.superuser;
  const [formModal, setFormModal] = useState<JSX.Element | null>(null);
  const popup = usePopupContext();

  // Stabilize callbacks with useCallback and proper deps
  const handleClearClauses = useCallback(() => {
    clauses.clearClauses();
  }, [clauses]);

  const handleExportCSV = useCallback(() => {
    downloadSearchStateAsCSV(searchState, app.org_id!);
  }, [searchState, app.org_id]);

  const handleShowSnippetForm = useCallback(() => {
    setFormModal(
      <SnippetFormControl
        onClose={() => setFormModal(null)}
        clauses={clauses.list}
      />
    );
  }, [clauses.list]);

  const handleShowQueryExplain = useCallback(
    (info: { domEvent: React.MouseEvent<HTMLElement> }) => {
      popup.setPopup(
        info.domEvent,
        <div style={{ maxWidth: "80vw", maxHeight: "80vh", overflow: "auto" }}>
          <QueryExplain autoResizePopup state={searchState} />
        </div>
      );
    },
    [popup, searchState]
  );

  // Memoize base menu items
  const baseItems = useMemo(() => {
    const items: MenuItem[] = [
      {
        key: "clear-clauses",
        label: <span style={{ fontSize: 13 }}>Start empty query</span>,
        icon: <ClearOutlined />,
        onClick: handleClearClauses,
      },
      { type: "divider" },
      isSuperUser
        ? {
            key: "explain-question",
            label: <span style={{ fontSize: 13 }}>Explain this query</span>,
            icon: <InfoCircleOutlined />,
            disabled: !isSuperUser,
            onClick: handleShowQueryExplain,
          }
        : null,
      {
        key: "export-csv",
        label: <span style={{ fontSize: 13 }}>Export as csv</span>,
        icon: <DownloadOutlined />,
        onClick: handleExportCSV,
      },
      {
        key: "create-snippet",
        label: <span style={{ fontSize: 13 }}>Create snippet from query</span>,
        icon: <ScissorOutlined />,
        onClick: handleShowSnippetForm,
      },
    ].filter(Boolean) as MenuItem[];

    return items;
  }, [
    isSuperUser,
    handleClearClauses,
    handleShowQueryExplain,
    handleExportCSV,
    handleShowSnippetForm,
  ]);

  // Memoize final menu items
  const items = useMemo(() => {
    if (resetIcon && resetTitle) {
      return [
        {
          key: "reset-clauses",
          label: <span style={{ fontSize: 13 }}>{resetTitle}</span>,
          icon: resetIcon,
          onClick: onReset,
        },
        ...baseItems.filter(
          (i) => !["create-snippet", "clear-clauses"].includes(i?.key as string)
        ),
      ];
    }
    return baseItems;
  }, [baseItems, resetIcon, resetTitle, onReset]);

  const tooltipTitle = useMemo(
    () => (collapsed ? "Expand" : "Collapse"),
    [collapsed]
  );
  const collapseIcon = useMemo(
    () => (collapsed ? <DownOutlined /> : <UpOutlined />),
    [collapsed]
  );

  const handleToggleCollapse = useCallback(() => {
    onToggleCollapse();
  }, [onToggleCollapse]);

  const handleApply = useCallback(() => {
    if (!disabledApply && onApply) {
      onApply();
    }
  }, [disabledApply, onApply]);

  // Memoize button props
  const collapseButtonProps = useMemo(
    () => ({
      shape: "round" as const,
      type: "text" as const,
      size: "small" as const,
      onClick: handleToggleCollapse,
      style: { padding: "0 8px" } as const,
    }),
    [handleToggleCollapse]
  );

  const resetButtonProps = useMemo(
    () => ({
      shape: "round" as const,
      type: "text" as const,
      size: "small" as const,
      disabled: disabledCancel,
      onClick: onReset,
      style: { padding: "0 8px" } as const,
    }),
    [disabledCancel, onReset]
  );

  const applyButtonProps = useMemo(
    () => ({
      className: styles.apply_button,
      shape: "round" as const,
      type: onApply && !disabledApply ? "primary" : "text",
      size: "small" as const,
      disabled: !onApply || disabledApply,
      onClick: handleApply,
      style: { padding: "0 8px" } as const,
    }),
    [onApply, disabledApply, handleApply]
  );

  return (
    <div
      className={isQueryEditor ? styles.queryEditorControls : styles.controls}
      style={{ marginRight: inlineResetOnly ? 10 : undefined }}
    >
      {formModal}
      {inlineResetOnly ? (
        <CustomTooltip
          content="Reset query"
          placement="left"
          mouseEnterDelay={0.5}
          mouseLeaveDelay={0.1}
          destroyTooltipOnHide
        >
          <Button {...resetButtonProps} style={{ borderRadius: 3 }}>
            <UndoOutlined />
          </Button>
        </CustomTooltip>
      ) : (
        <Space.Compact style={{ borderRadius: 16 }}>
          <CustomTooltip
            content={tooltipTitle}
            mouseEnterDelay={0.5}
            mouseLeaveDelay={0.1}
            destroyTooltipOnHide
          >
            <Button {...collapseButtonProps}>{collapseIcon}</Button>
          </CustomTooltip>
          {!disabledCancel && (
            <CustomTooltip
              content={resetTitle || "Cancel"}
              mouseEnterDelay={0.5}
              mouseLeaveDelay={0.1}
              destroyTooltipOnHide
            >
              <Button {...resetButtonProps}>
                {resetIcon || <StopOutlined />}
              </Button>
            </CustomTooltip>
          )}
          {onApply && (
            <CustomTooltip
              content={applyLabel}
              mouseEnterDelay={0.5}
              mouseLeaveDelay={0.1}
              destroyTooltipOnHide
            >
              <Button
                {...applyButtonProps}
                type={applyButtonProps.type as "primary"}
              >
                <CheckOutlined />
              </Button>
            </CustomTooltip>
          )}
        </Space.Compact>
      )}

      <Dropdown trigger={["click"]} menu={{ items }}>
        <Button
          type="text"
          size="small"
          style={{ padding: "0 6px", marginRight: collapsed ? 4 : 0 }}
        >
          <EllipsisOutlined />
        </Button>
      </Dropdown>
    </div>
  );
}

export const downloadSearchStateAsCSV = (
  searchState: SearchState,
  orgId: string
) => {
  if (searchState.groupBys?.length) {
    groupEvents({ ...searchState, format: "csv" }, orgId).then(({ data }) => {
      downloadAsFile({
        data: data as any,
        fileName: "vzbl_export.csv",
        type: "text/csv",
      });
    });
  } else {
    getEntities({ ...searchState, format: "csv" }, 1, 1000, orgId).then(
      ({ data }) => {
        downloadAsFile({
          data: data as any,
          fileName: "vzbl_export.csv",
          type: "text/csv",
        });
      }
    );
  }
};
